// Self-owned notification settings, stored at
// `users/{uid}/settings/notifications`: where notifications go (`channel`),
// the person's quiet hours (`quietHours`) and the photo-reminder opt-out.
//
// Mirrors lib/preferences.ts: the user fully owns this doc and may read/write
// it directly (see firestore.rules). It holds pure delivery preference and
// MUST NEVER drive an authorization or compliance decision. The server reads
// it (Admin SDK): scheduledPushDelivery routes by the channel, and
// queueNotification holds notifications back during the quiet hours. The
// in-app notification center is written regardless of either.
//
// A missing `channel` is not a value to fill in here: the default depends on
// whether the person has a push device, so both sides ask
// `effectiveNotificationChannel` (@onlogic/shared) instead.

import { doc, getDoc, setDoc } from 'firebase/firestore';
import type { NotificationChannel, NotificationSettings, QuietHours } from '@onlogic/shared';

import { db } from './firebase';

// One stable doc id under the per-user `settings` subcollection.
const NOTIFICATION_SETTINGS_DOC_ID = 'notifications';

/** Firestore reference to the active user's notification-settings document. */
export function notificationSettingsRef(uid: string) {
  return doc(db, 'users', uid, 'settings', NOTIFICATION_SETTINGS_DOC_ID);
}

/** TanStack Query key for a user's notification settings. */
export function notificationSettingsQueryKey(uid: string | undefined) {
  return ['notificationSettings', uid] as const;
}

/** Read the active user's notification settings, or an empty object if unset. */
export async function fetchNotificationSettings(uid: string): Promise<NotificationSettings> {
  const snap = await getDoc(notificationSettingsRef(uid));
  return snap.exists() ? (snap.data() as NotificationSettings) : {};
}

/**
 * Persist where notifications go. Merged write so we never clobber the other
 * fields (quiet hours, the photo-reminder flag), and so the document is
 * created on first write.
 */
export async function setNotificationChannel(
  uid: string,
  channel: NotificationChannel,
): Promise<void> {
  await setDoc(notificationSettingsRef(uid), { channel }, { merge: true });
}

/** This device's IANA time zone ("Europe/Amsterdam"), if the runtime can say. */
export function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Persist the person's quiet hours, stamped with this device's time zone — the
 * zone the window is evaluated in (their site's is the fallback when none is
 * saved). The whole window is written each time so its fields always agree.
 * Returns what was written, for the optimistic cache update.
 */
export async function setQuietHours(
  uid: string,
  window: { enabled: boolean; start: number; end: number },
): Promise<QuietHours> {
  const timeZone = deviceTimeZone();
  const quietHours: QuietHours = {
    enabled: window.enabled,
    start: window.start,
    end: window.end,
    ...(timeZone ? { timeZone } : {}),
  };
  await setDoc(notificationSettingsRef(uid), { quietHours }, { merge: true });
  return quietHours;
}

/**
 * Persist the self-serve opt-out for the weekly missing-photo reminder
 * (IMP-032). `dismissed === true` means "don't remind me". Merged so it never
 * clobbers the delivery `channel`.
 */
export async function setDismissedPhotoReminder(
  uid: string,
  dismissed: boolean,
): Promise<void> {
  await setDoc(notificationSettingsRef(uid), { dismissedPhotoReminder: dismissed }, { merge: true });
}
